import React, { useRef, useState, useCallback, useLayoutEffect, useEffect } from 'react';
import { t } from '@lingui/macro';
import {
  getRecaptchaHost,
  CaptchaOptions,
  getCaptchaOptions,
  initBaseCss,
  hideCaptcha,
  showCaptcha,
} from '../utils/captcha';
import { injectScript } from '$/utils/script';
import { useHasChangedShallow } from './useHasChanged';
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    grecaptcha: any;
  }
}

const useCaptcha = (
  sitekey: string,
  options: CaptchaOptions = {}
): {
  captchaRef: React.RefObject<HTMLDivElement>;
  execute: () => Promise<string | null>;
  response: string | null;
  captchaId: React.MutableRefObject<number | null>;
} => {
  const captchaRef = useRef(null);
  const captchaId = useRef<number | null>(null);
  const executeResolverRef = useRef<((response: string | null) => void) | null>(null);
  const executeRejectRef = useRef<((response: string) => void) | null>(null);
  const [response, setResponse] = useState<string | null>(null);

  const expiredCallback = useCallback(() => {
    console.error('CAPTCHA TIMEOUT');

    options.expiredCallback && options.expiredCallback();
    executeRejectRef.current &&
      executeRejectRef.current(t`reCaptcha expired: please retry submitting`);
  }, [options]);

  const errorCallback = useCallback(
    (error?: string) => {
      console.error('CAPTCHA ERROR: ', error);

      options.errorCallback && options.errorCallback(error);
      executeRejectRef.current &&
        executeRejectRef.current(t`reCaptcha error: please refresh and try again`);
    },
    [options]
  );

  const callback = useCallback(
    (capResponse: string | null): void => {
      options.size !== 'invisible' && hideCaptcha(captchaRef.current);
      setResponse(capResponse);

      if (typeof options.callback === 'function') {
        options.callback(capResponse);
      }

      if (captchaId.current !== null) {
        window.grecaptcha.reset(captchaId.current);
      }
    },
    [options]
  );

  useEffect(() => {
    if (response && executeResolverRef.current) {
      executeResolverRef.current(response);
    }
  }, [response]);

  const execute = React.useCallback((): Promise<string | null> => {
    return new Promise<string | null>((resolve, reject) => {
      setResponse(null);

      executeResolverRef.current = resolve;
      executeRejectRef.current = reject;

      const size = options.size;
      const isInvisibleSize = size === 'invisible';

      if (!window.grecaptcha || !window.grecaptcha.execute) {
        reject(t`reCaptcha error: please refresh and try again`);
        return;
      }

      isInvisibleSize
        ? window.grecaptcha.execute(captchaId.current)
        : showCaptcha(captchaRef.current, size);
    });
  }, [options.size]);

  const initCaptcha = React.useCallback((): void => {
    if (!options.enabled) return;

    // eslint-disable-next-line prefer-const
    let timerId: NodeJS.Timeout;

    const src = getRecaptchaHost(options.globally);
    if (typeof window?.grecaptcha === 'undefined') injectScript(src);

    const renderCaptcha = (): void => {
      if (
        typeof window.grecaptcha !== 'undefined' &&
        window.grecaptcha.render &&
        captchaRef.current
      ) {
        const captchaOptions = getCaptchaOptions(sitekey, {
          ...options,
          callback,
          errorCallback,
          expiredCallback,
        });

        captchaId.current = window.grecaptcha.render(captchaRef.current, captchaOptions);
        clearInterval(timerId);
      }
    };

    timerId = setInterval(renderCaptcha, 824);
  }, [callback, errorCallback, expiredCallback, options, sitekey]);

  const hasEnabledChanged = useHasChangedShallow([options.enabled]);
  useLayoutEffect(() => {
    if (!hasEnabledChanged || !options.enabled) return;

    initBaseCss(captchaRef.current);
    initCaptcha();
  }, [hasEnabledChanged, initCaptcha, options.enabled]);

  if (!options.enabled) {
    return {
      captchaRef,
      execute: async (): Promise<string | null> => {
        options.callback && options.callback(null);
        return null;
      },
      response,
      captchaId,
    };
  }

  return { captchaRef, execute, response, captchaId };
};

export default useCaptcha;
